import { ButtonProps, Modal } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Column, ReadOnlyItem, Row, Text } from '@ydistri/ds';
import { TemplateSelect } from '../../Configuration/TemplateSelect';
import { useLoadCalculationToTemplateMutation } from '../apiCalculations';
import { addToast } from '@ydistri/utils';
import { generatePath, useNavigate } from 'react-router';
import { RoutingPage } from '../../../routes/routerSlice';
import { ConfigurationSubpage } from '../../Configuration/Configuration';
import { useGetTemplatesQuery } from '../../../apis/apiApplication';
import { getSelectedProjectShortName } from '../../../apis/apiHeader';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetCalculationQuery } from '../../CalculationDetail/apiCalculationDetail';
import { styled } from 'styled-components';
import { Overlay } from '../../../components/global/Overlay';
import OverlayMessage, {
  OverlayMessageIcon,
} from '../../../components/global/ContentOverlay/OverlayComponents';
import { ErrorType } from '../../../apis/api';
import { useAppDispatch } from '../../../store';
import { useStableParams } from '../../../routes/useStableParams';
import { useGetCalculationConfigurationQuery } from '../../../apis/apiGlobalConfiguration';
import { isValidTemplateId } from '../../../lib/templates/templatesLib';

const MODAL_LABEL_WIDTH = '10rem';
const TEMPLATE_SELECT_LABEL_STYLE = { marginTop: '0.5rem', display: 'inline-block' };

const loadingOverlayContent = <OverlayMessage title="Loading" icon={OverlayMessageIcon.SPINNER} />;

const SelectCalculationTemplateText = styled(Text)`
  cursor: pointer;
  margin-left: auto;
  display: inline-block;
  margin-right: 0.5rem;
  color: ${({ theme }) => theme.colors.primary};
`;

interface UseCalculationTemplateActionProps {
  templateId: number;
  onClick: (templateId: number) => void;
}

/**
 * Action to select the template from the calculation.
 * @param template Source template of the calculation
 * @param onClick event handler called when user clicks the action
 * @constructor
 */
const SelectCalculationTemplateAction: React.FC<UseCalculationTemplateActionProps> = ({
  templateId,
  onClick,
}) => {
  const { data: templateConfiguration, isFetching: isFetchingTemplateConfig } =
    useGetCalculationConfigurationQuery(
      isValidTemplateId(templateId)
        ? {
            type: 'Template',
            id: templateId,
          }
        : skipToken,
    );

  const clickHandler = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (templateId) {
        onClick(templateId);
      }
    },
    [templateId, onClick],
  );

  if (!isFetchingTemplateConfig) {
    if (!templateConfiguration?.isLocked) {
      return (
        <SelectCalculationTemplateText onClick={clickHandler}>
          Use target template from calculation
        </SelectCalculationTemplateText>
      );
    } else {
      return (
        <SelectCalculationTemplateText>{`Template with Id: ${templateId} from calculation is locked and cannot be used`}</SelectCalculationTemplateText>
      );
    }
  }
};

interface LoadIntoTemplateModalProps {
  calculationId: number;
  calculationTitle: string;
  onConfirmed?: () => void;
  onCanceled: () => void;
}

const LoadIntoTemplateModal: React.FC<LoadIntoTemplateModalProps> = ({
  calculationId,
  calculationTitle,
  onConfirmed,
  onCanceled,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { projectShortName } = useStableParams();
  const [templateId, setTemplateId] = React.useState<number | undefined>();
  const [loadCalculationToTemplate, loadCalculationToTemplateStatus] =
    useLoadCalculationToTemplateMutation({
      fixedCacheKey: 'loading-to-template',
    });

  const { data: templates, isFetching: isTemplatesFetching } = useGetTemplatesQuery(
    getSelectedProjectShortName() ?? skipToken,
  );
  const { data: calculation, isFetching: isCalculationFetching } =
    useGetCalculationQuery(calculationId);

  useEffect(() => {
    if (templates?.length === 1) {
      setTemplateId(templates[0].id);
    }
  }, [templates]);

  const cancelHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onCanceled();
    },
    [onCanceled],
  );

  const okHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (templateId) {
        loadCalculationToTemplate({
          fromCalculationId: calculationId,
          toTemplateId: templateId,
        })
          .unwrap()
          .then(() => {
            if (onConfirmed) onConfirmed();
            dispatch(
              addToast({
                message: `Configuration from calculation #${calculationId} was loaded`,
              }),
            );

            if (projectShortName) {
              navigate(
                generatePath(
                  `/:projectShortName/${RoutingPage.CONFIGURATION}/:templateId/${ConfigurationSubpage.CATEGORIES}/1`,
                  { templateId: templateId.toString(), projectShortName },
                ),
              );
            }
          })
          // eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable -- we know the type
          .catch((error: ErrorType) => {
            dispatch(
              addToast({
                message: `Configuration from calculation #${calculationId} was not loaded: ${error.response.data.Messages.join(
                  ', ',
                )}`,
                isError: true,
              }),
            );
          });
      } else {
        onCanceled();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navigate is not a dependency
    [
      templateId,
      onConfirmed,
      calculationId,
      loadCalculationToTemplate,
      dispatch,
      projectShortName,
      onCanceled,
    ],
  );

  //The modal is wrapped in a div to prevent the click event from bubbling up to the parent
  //and selecting the calculation below the modal
  const onWrapperClick = useCallback((event: React.MouseEvent) => event.stopPropagation(), []);

  const okButtonProps: ButtonProps = useMemo(() => {
    const isDisabled = !templateId; //if the template is not selected, the OK button is disabled

    return {
      disabled: isDisabled,
    };
  }, [templateId]);

  const onSelectCalculationTemplate = useCallback((templateId: number) => {
    setTemplateId(templateId);
  }, []);

  return (
    <div onClick={onWrapperClick}>
      <Modal
        data-type="modal"
        open={true}
        title="Load configurations"
        closable={false}
        onCancel={cancelHandler}
        onOk={okHandler}
        confirmLoading={loadCalculationToTemplateStatus.isLoading}
        okText="Load"
        okButtonProps={okButtonProps}
        maskClosable={false}
        width="60rem"
      >
        <Column>
          <Row>
            <Text>
              Load configurations from already computed calculation to template selected below.
            </Text>
          </Row>
          <Overlay
            active={isCalculationFetching || isTemplatesFetching}
            overlayContent={loadingOverlayContent}
          >
            <Column $padding={4} $gap="1rem">
              <ReadOnlyItem
                label="Source calculation"
                labelWidth={MODAL_LABEL_WIDTH}
                value={`#${calculationId}: ${calculationTitle}`}
              />
              <ReadOnlyItem
                label="Target template"
                labelWidth={MODAL_LABEL_WIDTH}
                alignItems="normal"
                labelStyle={TEMPLATE_SELECT_LABEL_STYLE}
              >
                <Column>
                  <TemplateSelect
                    onChange={setTemplateId}
                    templateId={templateId}
                    unlockedOnly={true}
                  />
                  {
                    //the use source template action makes sense only when there is a source template and there are more than
                    // one template because if only one template is available then it is already selected
                  }
                  {calculation?.sourceTemplate && templates && templates.length > 1 && (
                    <SelectCalculationTemplateAction
                      templateId={calculation.sourceTemplate.id}
                      onClick={onSelectCalculationTemplate}
                    />
                  )}
                </Column>
              </ReadOnlyItem>
            </Column>
          </Overlay>
        </Column>
      </Modal>
    </div>
  );
};

export default LoadIntoTemplateModal;
